import { forwardRef, useState } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faImageSlash } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { twJoin } from "tailwind-merge";

import { Icon } from "src/design-system/Icon";

export type NodeIconSize = "xs" | "sm" | "md" | "lg" | "xl";

export type NodeIconColor =
  | "green-electric"
  | "indigo-200"
  | "indigo-100"
  | "yellow-200"
  | "sky-200"
  | "orange-200"
  | "pink-200"
  | "green-200"
  | "orange-100"
  | "yellow-100"
  | "gray-200"
  | "outlined";

export type SvgIconComponent = React.ComponentType<{ className?: string }>;

type BaseNodeIconProps = {
  size?: NodeIconSize;
  dataLoc?: string;
};

type FontAwesomeVariantProps = BaseNodeIconProps & {
  icon: IconProp;
  color?: NodeIconColor;
  svg?: never;
  imageSrc?: never;
};

type SvgVariantProps = BaseNodeIconProps & {
  svg: SvgIconComponent;
  icon?: never;
  color?: never;
  imageSrc?: never;
};

type ImageVariantProps = BaseNodeIconProps & {
  imageSrc: string;
  alt?: string;
  icon?: never;
  color?: never;
  svg?: never;
};

const isSvgVariantProps = (props: NodeIconProps): props is SvgVariantProps =>
  "svg" in props;
const isImageVariantProps = (
  props: NodeIconProps,
): props is ImageVariantProps => "imageSrc" in props;

export type NodeIconProps =
  | FontAwesomeVariantProps
  | SvgVariantProps
  | ImageVariantProps;

const SIZE_STYLES: Record<NodeIconSize, { container: string; icon: string }> = {
  xs: { container: "w-4 h-4 rounded", icon: "w-2 h-2" },
  sm: { container: "w-5 h-5 rounded", icon: "w-2.5 h-2.5" },
  md: { container: "w-6 h-6 rounded", icon: "w-2.5 h-2.5" },
  lg: { container: "w-10 h-10 rounded-lg", icon: "w-4.5 h-4.5" },
  xl: { container: "w-12 h-12 rounded-xl", icon: "w-4.5 h-4.5" },
} as const;

export const COLOR_STYLES: Record<NodeIconColor, string> = {
  "green-electric": "bg-green-electric",
  "indigo-200": "bg-indigo-200",
  "indigo-100": "bg-indigo-100",
  "yellow-200": "bg-yellow-200",
  "sky-200": "bg-sky-200",
  "orange-200": "bg-orange-200",
  "pink-200": "bg-pink-200",
  "green-200": "bg-green-200",
  "orange-100": "bg-orange-100",
  "yellow-100": "bg-yellow-100",
  "gray-200": "bg-gray-200",
  outlined: "border border-gray-200 bg-white",
} as const;

const ImageNodeIcon = forwardRef<
  HTMLSpanElement,
  {
    imageSrc: string;
    alt?: string;
    dataLoc?: string;
    size: NodeIconSize;
  }
>(({ imageSrc, alt = "", dataLoc, size }, ref) => {
  const [isFailed, setIsFailed] = useState(false);
  const sizeStyle = SIZE_STYLES[size];

  return (
    <span
      ref={ref}
      className={twJoin(
        "flex items-center justify-center overflow-hidden",
        sizeStyle.container,
        COLOR_STYLES["outlined"],
        isFailed && "bg-gray-100",
      )}
      data-loc={dataLoc}
    >
      {!isFailed ? (
        <img
          className="h-full w-full object-contain"
          alt={alt}
          src={imageSrc}
          onError={() => setIsFailed(true)}
        />
      ) : (
        <Icon icon={faImageSlash} size="md" variant="disabled" />
      )}
    </span>
  );
});

export const NodeIcon = forwardRef<HTMLSpanElement, NodeIconProps>(
  (props, ref) => {
    const { size = "lg", dataLoc } = props;
    const sizeStyle = SIZE_STYLES[size];

    if (isSvgVariantProps(props)) {
      const SvgComponent = props.svg;
      return (
        <span
          ref={ref}
          className={twJoin(
            "flex shrink-0 items-center justify-center overflow-hidden",
            COLOR_STYLES["outlined"],
            sizeStyle.container,
          )}
          data-loc={dataLoc}
        >
          <SvgComponent className={sizeStyle.container} />
        </span>
      );
    }

    if (isImageVariantProps(props)) {
      return (
        <ImageNodeIcon
          ref={ref}
          alt={props.alt}
          dataLoc={dataLoc}
          imageSrc={props.imageSrc}
          size={size}
        />
      );
    }

    return (
      <span
        ref={ref}
        className={twJoin(
          "flex shrink-0 items-center justify-center",
          sizeStyle.container,
          COLOR_STYLES[props.color ?? "outlined"],
        )}
        data-loc={dataLoc}
      >
        <FontAwesomeIcon
          className={twJoin("aspect-square text-gray-800", sizeStyle.icon)}
          icon={props.icon}
        />
      </span>
    );
  },
);
